import { useRef, useCallback } from 'react';
import { util } from 'fabric';

/**
 * useCanvasHistory — Snapshot-based undo/redo for Fabric.js canvas.
 *
 * Takes snapshots of canvas JSON state on meaningful actions and restores them
 * on undo/redo. Layers are rebuilt from canvas objects after restore.
 *
 * Usage:
 *   const { saveSnapshot, undo, redo, canUndo, canRedo } = useCanvasHistory(fabricRef, setLayers, setSelectedLayer);
 *
 * Call saveSnapshot() after any meaningful action (object added, modified, deleted, etc.)
 */
const useCanvasHistory = (fabricRef, setLayers, setSelectedLayer) => {
    const undoStack = useRef([]);
    const redoStack = useRef([]);
    const isRestoring = useRef(false);
    const MAX_HISTORY = 50;

    /**
     * Take a snapshot of the current canvas state.
     * Call this after object:added, object:removed, object:modified, etc.
     */
    const saveSnapshot = useCallback(() => {
        const canvas = fabricRef.current;
        if (!canvas || isRestoring.current) return;

        // Serialize canvas — exclude helper objects (guides, snap lines, etc.)
        const objects = canvas.getObjects().filter(o => !o.excludeFromExport && !o._isCustomGuide && !o._isSnapGuide);
        const snapshot = {
            objects: objects.map(o => o.toJSON(['_layerName', '_layerId'])),
            canvasWidth: canvas.width,
            canvasHeight: canvas.height,
        };

        undoStack.current.push(JSON.stringify(snapshot));
        if (undoStack.current.length > MAX_HISTORY) {
            undoStack.current.shift();
        }
        // Clear redo on new action
        redoStack.current = [];
    }, [fabricRef]);

    /**
     * Restore a snapshot to the canvas, rebuilding layers state.
     */
    const restoreSnapshot = useCallback(async (snapshotStr) => {
        const canvas = fabricRef.current;
        if (!canvas) return;

        isRestoring.current = true;
        try {
            const snapshot = JSON.parse(snapshotStr);
            const objectsData = snapshot.objects || [];

            // 1. Pre-load new objects in memory (async)
            // This prevents the canvas from being empty while loading
            const enlivenedObjects = await util.enlivenObjects(objectsData);

            // Restore custom properties manually to ensure they persist
            enlivenedObjects.forEach((obj, i) => {
                const data = objectsData[i];
                if (data._layerId) obj._layerId = data._layerId;
                if (data._layerName) obj._layerName = data._layerName;
                // Ensure locking state is preserved
                if (data.locked) {
                    obj.selectable = false;
                    obj.evented = false;
                }
            });

            // 2. Synchronous Swap
            // Disable rendering to prevent flicker
            canvas.renderOnAddRemove = false;

            // Remove old objects (keep guides)
            const toRemove = canvas.getObjects().filter(o => !o.excludeFromExport && !o._isCustomGuide && !o._isSnapGuide);
            toRemove.forEach(o => canvas.remove(o));

            // Add new objects
            enlivenedObjects.forEach((obj) => {
                canvas.add(obj);
            });

            // Fix Z-order: Ensure guides are on top
            canvas.getObjects().forEach(o => {
                if ((o._isCustomGuide || o._isSnapGuide) && typeof o.bringToFront === 'function') {
                    o.bringToFront();
                }
            });

            // 3. Re-enable rendering and paint once
            canvas.renderOnAddRemove = true;

            // Rebuild layers from new canvas objects
            const restoredLayers = canvas.getObjects()
                .filter(o => !o.excludeFromExport && !o._isCustomGuide && !o._isSnapGuide)
                .map((obj, i) => ({
                    id: obj._layerId || `layer_${Date.now()}_${i}`,
                    name: obj._layerName || `Layer ${i + 1}`,
                    object: obj,
                    visible: obj.visible !== false,
                    locked: !obj.selectable,
                    opacity: obj.opacity || 1,
                }));

            setLayers(restoredLayers);
            setSelectedLayer(null);
            canvas.discardActiveObject();
            canvas.requestRenderAll();
        } catch (e) {
            console.error('History restore failed:', e);
            // Ensure render is re-enabled if error
            canvas.renderOnAddRemove = true;
        } finally {
            isRestoring.current = false;
        }
    }, [fabricRef, setLayers, setSelectedLayer]);

    /**
     * Undo the last action.
     */
    const undo = useCallback(async () => {
        if (undoStack.current.length < 2) return; // Need at least 2: current + previous

        const canvas = fabricRef.current;
        if (!canvas || isRestoring.current) return;

        // Move current state to redo stack
        const currentState = undoStack.current.pop();
        redoStack.current.push(currentState);

        // Restore previous state
        const previousState = undoStack.current[undoStack.current.length - 1];
        await restoreSnapshot(previousState);
    }, [fabricRef, restoreSnapshot]);

    /**
     * Redo the last undone action.
     */
    const redo = useCallback(async () => {
        if (redoStack.current.length === 0) return;

        const canvas = fabricRef.current;
        if (!canvas || isRestoring.current) return;

        // Pop from redo and push to undo
        const nextState = redoStack.current.pop();
        undoStack.current.push(nextState);

        // Restore that state
        await restoreSnapshot(nextState);
    }, [fabricRef, restoreSnapshot]);

    return {
        saveSnapshot,
        undo,
        redo,
        canUndo: undoStack.current.length >= 2,
        canRedo: redoStack.current.length > 0,
        isRestoring,
    };
};

export default useCanvasHistory;
